/**
 * Fazla Mesai (Overtime) Sabitleri
 * 
 * Bu dosya, overtime sistemi için kullanılan sabit değerleri içerir.
 * Durum konfigürasyonları, renk paletleri, ikonlar vb.
 */

import { OvertimeStatus } from '../types/overtime.types';

/**
 * Overtime durum badge konfigürasyonları
 * Her durum için renk, ikon ve metin tanımları
 */
export const OVERTIME_STATUS_CONFIG = {
  [OvertimeStatus.PENDING]: {
    text: 'Beklemede',
    textEn: 'Pending',
    color: 'badge-warning', // Ant Design badge class
    bgColor: 'bg-yellow-100', // TailwindCSS class
    textColor: 'text-yellow-800',
    icon: '🟡',
    iconClass: 'ti ti-clock',
  },
  [OvertimeStatus.APPROVED]: {
    text: 'Onaylandı',
    textEn: 'Approved',
    color: 'badge-success',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    icon: '✅',
    iconClass: 'ti ti-check',
  },
  [OvertimeStatus.REJECTED]: {
    text: 'Reddedildi',
    textEn: 'Rejected',
    color: 'badge-danger',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    icon: '❌',
    iconClass: 'ti ti-x',
  },
};

/**
 * Overtime sistemi mod açıklamaları
 * Departman ayarları için kullanılır
 */
export const OVERTIME_MODE_INFO = {
  automatic: {
    title: 'Otomatik (Eski Sistem)',
    titleEn: 'Automatic (Old System)',
    description: 'Çalışan çıkış yaptığında sistem otomatik hesaplar ve onaylar.',
    descriptionEn: 'System automatically calculates and approves when employee checks out.',
    icon: 'ti ti-automatic',
  },
  manual: {
    title: 'Manuel Talep Sistemi',
    titleEn: 'Manual Request System',
    description: 'Çalışan talep oluşturur, yönetici onaylar. Otomatik çıkış aktif.',
    descriptionEn: 'Employee creates request, manager approves. Auto checkout active.',
    icon: 'ti ti-hand-stop',
    benefits: [
      'Çalışan fazla mesai talebi oluşturur',
      'Yönetici onayı gerekir',
      'Çıkış unutulursa otomatik çıkış yapılır (15dk tolerans)',
      'Bordro\'ya sadece onaylı kayıtlar dahil',
    ],
    benefitsEn: [
      'Employee creates overtime request',
      'Manager approval required',
      'Auto checkout if forgot (15min tolerance)',
      'Only approved records included in payroll',
    ],
  },
};

/**
 * Zaman periyodu seçenekleri
 * Dashboard ve raporlama için kullanılır
 */
export const TIME_PERIOD_OPTIONS = [
  { value: 'day', label: 'Bugün', labelEn: 'Today' },
  { value: 'week', label: 'Bu Hafta', labelEn: 'This Week' },
  { value: 'month', label: 'Bu Ay', labelEn: 'This Month' },
  { value: 'year', label: 'Bu Yıl', labelEn: 'This Year' },
];

/**
 * Durum filtre seçenekleri
 * Overtime listesi filtreleme için kullanılır
 */
export const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'Tüm Durumlar', labelEn: 'All Status' },
  { value: OvertimeStatus.PENDING, label: 'Beklemede', labelEn: 'Pending' },
  { value: OvertimeStatus.APPROVED, label: 'Onaylı', labelEn: 'Approved' },
  { value: OvertimeStatus.REJECTED, label: 'Reddedildi', labelEn: 'Rejected' },
];

/**
 * Pagination için varsayılan değerler
 */
export const OVERTIME_PAGINATION_DEFAULTS = {
  page: 1,
  perPage: 20,
  pageSizeOptions: [10, 20, 50, 100],
};

/**
 * Tarih format sabitleri
 */
export const OVERTIME_DATE_FORMATS = {
  display: 'DD.MM.YYYY',        // Ekranda gösterim için
  displayWithDay: 'DD MMMM YYYY (dddd)', // Gün adı ile
  api: 'YYYY-MM-DD',            // API istekleri için
  time: 'HH:mm',                // Saat formatı
  datetime: 'DD.MM.YYYY HH:mm', // Tarih ve saat
};

/**
 * Süre formatı yardımcı fonksiyonları için sabitler
 */
export const DURATION_CONSTANTS = {
  minutesInHour: 60,
  minutesInDay: 1440,
  hoursInDay: 24,
};

/**
 * Onay/Red modal mesajları
 */
export const OVERTIME_MODAL_MESSAGES = {
  approve: {
    title: 'Fazla Mesai Talebini Onayla',
    titleEn: 'Approve Overtime Request',
    confirmText: 'Bu talebi onaylamak istediğinizden emin misiniz?',
    confirmTextEn: 'Are you sure you want to approve this request?',
    warningText: 'Bu talebi onayladığınızda:',
    warningTextEn: 'When you approve this request:',
    warnings: [
      'Çalışan belirtilen tarih/saatte fazla mesai yapabilir',
      'Bordro\'ya eklenecek',
      'Çalışana bildirim gönderilecek',
    ],
    warningsEn: [
      'Employee can work overtime on specified date/time',
      'Will be added to payroll',
      'Notification will be sent to employee',
    ],
  },
  reject: {
    title: 'Fazla Mesai Talebini Reddet',
    titleEn: 'Reject Overtime Request',
    reasonLabel: 'Red Nedeni:',
    reasonLabelEn: 'Rejection Reason:',
    reasonPlaceholder: 'Red nedenini açıklayın...',
    reasonPlaceholderEn: 'Explain rejection reason...',
    reasonRequired: 'Red nedeni zorunludur',
    reasonRequiredEn: 'Rejection reason is required',
  },
};

/**
 * Toast mesaj şablonları
 */
export const OVERTIME_TOAST_MESSAGES = {
  approveSuccess: 'Fazla mesai talebi başarıyla onaylandı',
  approveSuccessEn: 'Overtime request approved successfully',
  approveError: 'Talep onaylanırken bir hata oluştu',
  approveErrorEn: 'An error occurred while approving request',
  
  rejectSuccess: 'Fazla mesai talebi reddedildi',
  rejectSuccessEn: 'Overtime request rejected',
  rejectError: 'Talep reddedilirken bir hata oluştu',
  rejectErrorEn: 'An error occurred while rejecting request',
  
  createSuccess: 'Fazla mesai tanımlandı',
  createSuccessEn: 'Overtime created successfully',
  createError: 'Fazla mesai tanımlanırken bir hata oluştu',
  createErrorEn: 'An error occurred while creating overtime',
  
  departmentUpdateSuccess: 'Departman ayarları güncellendi',
  departmentUpdateSuccessEn: 'Department settings updated',
  departmentUpdateError: 'Ayarlar güncellenirken bir hata oluştu',
  departmentUpdateErrorEn: 'An error occurred while updating settings',
};

/**
 * Validasyon mesajları
 */
export const OVERTIME_VALIDATION_MESSAGES = {
  employeeRequired: 'Çalışan seçimi zorunludur',
  employeeRequiredEn: 'Employee selection is required',
  dateRequired: 'Tarih seçimi zorunludur',
  dateRequiredEn: 'Date selection is required',
  startTimeRequired: 'Başlangıç saati zorunludur',
  startTimeRequiredEn: 'Start time is required',
  endTimeRequired: 'Bitiş saati zorunludur',
  endTimeRequiredEn: 'End time is required',
  endTimeAfterStart: 'Bitiş saati başlangıçtan sonra olmalı',
  endTimeAfterStartEn: 'End time must be after start time',
  descriptionMaxLength: 'Açıklama maksimum 500 karakter olabilir',
  descriptionMaxLengthEn: 'Description can be maximum 500 characters',
};

/**
 * UI metinleri
 */
export const OVERTIME_UI_TEXT = {
  noPendingRequests: 'Bekleyen talep bulunmamaktadır',
  noPendingRequestsEn: 'No pending requests',
  noRecords: 'Kayıt bulunamadı',
  noRecordsEn: 'No records found',
  loading: 'Yükleniyor...',
  loadingEn: 'Loading...',
  searchPlaceholder: 'Ara...',
  searchPlaceholderEn: 'Search...',
  filterByDepartment: 'Departmana Göre Filtrele',
  filterByDepartmentEn: 'Filter by Department',
  filterByStatus: 'Duruma Göre Filtrele',
  filterByStatusEn: 'Filter by Status',
  exportToExcel: 'Excel\'e Aktar',
  exportToExcelEn: 'Export to Excel',
  refresh: 'Yenile',
  refreshEn: 'Refresh',
  details: 'Detay',
  detailsEn: 'Details',
  approve: 'Onayla',
  approveEn: 'Approve',
  reject: 'Reddet',
  rejectEn: 'Reject',
  cancel: 'İptal',
  cancelEn: 'Cancel',
  save: 'Kaydet',
  saveEn: 'Save',
  confirm: 'Onayla',
  confirmEn: 'Confirm',
};


